// RTK Query slice for opening hours management
// Derives data from setupApi (api/auth-restaurant) - no separate API calls
import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "./baseQuery";
import { setupApi } from "./setupApi";

export const openingHoursApi = createApi({
  reducerPath: "openingHoursApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["OpeningHours"],
  endpoints: (builder) => ({
    /**
     * Get opening hours for a specific meal type
     * This doesn't make a new API call - it reads from setupApi's cache
     */
    getOpeningHours: builder.query({
      queryFn: async (mealType, { getState }, _extraOptions, baseQuery) => {
        // Get data from setupApi cache instead of making a new API call
        const setupState = setupApi.endpoints.getRestaurantSetup.select()(getState());

        // If setupApi has an error, propagate it
        if (setupState.error) {
          return { error: setupState.error };
        }

        // Extract data from setupApi cache (even if still loading, return what we have)
        const openingHoursData = setupState.data?.openingHours?.[mealType];
        const schemeSettings = openingHoursData?.schemeSettings || {};

        return {
          data: schemeSettings,
        };
      },
      providesTags: (result, error, mealType) => [
        { type: "OpeningHours", id: mealType },
      ],
    }),

    /** Update opening hours for a specific meal type */
    updateOpeningHours: builder.mutation({
      query: ({ mealType, ...body }) => ({
        url: `api/openinghours-${mealType}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { mealType }) => [
        { type: "OpeningHours", id: mealType },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate setupApi to refetch the centralized data
          dispatch(setupApi.util.invalidateTags(["RestaurantSetup"]));
        } catch {}
      },
    }),
  }),
});

export const { useGetOpeningHoursQuery, useUpdateOpeningHoursMutation } =
  openingHoursApi;
